import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function WheelchairTransport() {
  const serviceSchema = generateServiceSchema(
    'Wheelchair Medical Transportation',
    'Specialized wheelchair-accessible medical transportation with trained drivers and equipped vehicles. Safe, comfortable transport for wheelchair users throughout Orange County and Southern California.'
  );

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/#services' },
    { name: 'Wheelchair Transport', url: '/services/wheelchair' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [serviceSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Wheelchair Medical Transportation Westminster CA | Accessible NEMT Services',
    description: 'Reliable wheelchair-accessible medical transportation in Westminster CA and Orange County. Specialized vehicles and trained drivers for safe, comfortable wheelchair transport. Available 24/7.',
    keywords: 'wheelchair transport, wheelchair accessible transportation, wheelchair medical transport, handicap transportation, accessible NEMT Westminster CA',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/wheelchair`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/f32bda6e736d3ef53bbfcaaef3c61b26.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Wheelchair Transportation Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Fully accessible vehicles with wheelchair lifts and secure tie-down systems for safe, comfortable transport
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => window.location.href = 'tel:+17147987318'}
                className="text-lg px-8 py-4"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Wheelchair Transport Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Wheelchair Transportation
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our wheelchair transportation service provides safe, dignified transport for patients who use wheelchairs or mobility scooters. We understand the unique needs of wheelchair users and have invested in the right equipment and training to serve you properly.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Every vehicle in our wheelchair transport fleet is equipped with hydraulic lifts, secure tie-down systems, and climate control to ensure your comfort and safety throughout the journey.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">ADA-compliant wheelchair lifts</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">4-point secure tie-down systems</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Trained wheelchair transport specialists</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Accommodates all wheelchair types and sizes</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/5878a177f35c5dae527c2ab6b796495f.png"
                alt="Wheelchair Transport Interior"
                className="w-full rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Features */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Wheelchair Transport Features
            </h2>
            <p className="text-xl text-gray-600">
              State-of-the-art equipment designed for your safety and comfort
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lift-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Hydraulic Lifts</h3>
              <p className="text-gray-600">
                Smooth, reliable hydraulic wheelchair lifts that safely raise and lower wheelchairs with minimal effort.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-anchor-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure Tie-Downs</h3>
              <p className="text-gray-600">
                Professional 4-point tie-down systems that meet all federal safety standards for wheelchair securement.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-temp-hot-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Climate Control</h3>
              <p className="text-gray-600">
                Individual climate control zones ensure comfortable temperature throughout your journey.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-focus-3-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wide Door Access</h3>
              <p className="text-gray-600">
                Extra-wide doors and low floors for easy wheelchair access and maneuvering space.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-wheelchair-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">All Wheelchair Types</h3>
              <p className="text-gray-600">
                Accommodates manual wheelchairs, power chairs, scooters, and bariatric wheelchairs up to 600 lbs.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-first-aid-kit-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Safety Equipment</h3>
              <p className="text-gray-600">
                Emergency equipment, first aid supplies, and communication systems for added safety and peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Wheelchair Transport Process
            </h2>
            <p className="text-xl text-gray-600">
              Step-by-step care designed around your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Pre-Trip Assessment</h3>
              <p className="text-gray-600">
                We assess your wheelchair type, mobility needs, and any special requirements during booking.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Safe Loading</h3>
              <p className="text-gray-600">
                Our trained driver uses the hydraulic lift and secure tie-down system for safe wheelchair loading.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Comfortable Transport</h3>
              <p className="text-gray-600">
                Smooth, comfortable ride with your wheelchair securely positioned and climate controlled for comfort.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Destination Assistance</h3>
              <p className="text-gray-600">
                Safe unloading at your destination with assistance navigating to your appointment if needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance & Billing */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Insurance & Payment Options
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We work with most insurance plans and offer flexible payment options to make wheelchair transportation accessible and affordable.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <i className="ri-hospital-line text-3xl text-blue-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Medicare/Medicaid</h3>
                <p className="text-gray-600">We're approved providers for Medicare and Medicaid wheelchair transport services.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <i className="ri-shield-line text-3xl text-green-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Private Insurance</h3>
                <p className="text-gray-600">We work with most major insurance providers and handle billing directly.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <i className="ri-money-dollar-circle-line text-3xl text-purple-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Private Pay</h3>
                <p className="text-gray-600">Competitive rates with flexible payment options including payment plans.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Reliable Wheelchair Transportation?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Trust InteCare for safe, professional wheelchair transport services. Our ADA-compliant vehicles and trained drivers ensure your comfort and safety.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}